/**
 * Mapeamento oficial de userName → Diretoria.
 * Fonte: planilha DIRETORIAS_260526.xlsx (Lucimar, 26/05/2026).
 *
 * Mudanças vs versão anterior (10/04/2026):
 *  - CERRADOS LESTE → MAPITOBAPANE
 *  - CERRADOS OESTE → MT RO
 *  - GO/MS NORTE     → GO MS NORTE (sem barra)
 *  - MG/ES/SB/FL     → MG ES SB FL (sem barras)
 *  - SP CANA         → SP/CANA (com barra)
 *  - BRASIL          → ICL
 *
 * Inclui: NOME oficial + "Outros usuários Nutro" + "Desligado/mudou função" como aliases
 * para cobrir dados históricos. Os UserName de Diego Rizzatto e Andressa Dalla Vecchia
 * foram migrados para "Douglas Mokfa" no banco em 26/05/2026; aliases mantidos por
 * segurança caso entrem leituras tardias.
 */

const USER_DIRETORIA: Record<string, string> = {};

function normalizeName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function add(diretoria: string, names: string[]) {
  for (const raw of names) {
    const name = raw.trim();
    if (!name) continue;
    USER_DIRETORIA[normalizeName(name)] = diretoria;
  }
}

// ─── ICL (antiga BRASIL) ───
add("ICL", [
  "João Pascoalino",
  "Michel Castellani",
  "Rafael Butke",
]);

// ─── CENTRO SUL ───
add("CENTRO SUL", [
  "Paulo Matchula",
  "Adrian Figueredo",
  "Bruna Rodak",
  "Cleyton Domingos",
  "Douglas Mokfa",
  "Luis Forgiani",
  "Luis Forgiarini",           // variação no banco (com r extra) — 6956 leituras
  "Luiz Faganello",
  "Marcos Besen",
  "Paulo Teixeira",
  "Rafael Mergener",
  // Aliases históricos / Outros usuários Nutro (mesma diretoria):
  "Jose Bazzo",                // desligado
  "Thiago Baggio",             // outro usuário Nutro do Paulo Matchula
  "Thiago Zanoni Bagio",       // variação no banco
  "Gabriel Juliano Jorge",     // outro usuário Nutro
  "Paulo Henrrique Matchula",  // outro usuário Nutro
  "Andersom Milech Einhardt",  // desligado
  "Andersom Einhardt",         // variação no banco
  "Alisson Cardoso",           // outro usuário Nutro do Douglas Mokfa
  "Diego Rizzatto",            // outro usuário Nutro do Douglas Mokfa
  "Andressa Dalla Vecchia",    // outro usuário Nutro do Douglas Mokfa
]);

// ─── MAPITOBAPANE (antiga CERRADOS LESTE) ───
add("MAPITOBAPANE", [
  "Fabio Ferreira",
  "Anna Flávia Lopes",
  "Anna Lopes",                // variação no banco
  "Antônio Gabriel Ferreira",
  "Ariel Siqueira",
  "Ariel Carvalho",            // mesma pessoa (mudou sobrenome) — confirmado por Carol/Lucimar 26/05
  "Cynthia Silva",
  "Karine Barbosa",
  "Klever Silveira",           // mudou função: virou Gestora MAPITOBAPANE
  // Aliases históricos:
  "Flavia Souza",              // outro usuário Nutro (sucessor: Fabio Ferreira)
]);

// ─── MT RO (antiga CERRADOS OESTE) ───
add("MT RO", [
  "Andressa Chagas",
  "Bruno Val",
  "Carlos Alberto Silva Junior",
  "Carlos Junior",             // variação no banco
  "Diego Amaro",
  "Gilson Felizati",
  "Gilson Filho",              // mesma pessoa — confirmado por Carol/Lucimar 26/05
  "Natasha Gomes Freitas",
  "Natasha Freitas",           // variação no banco
  "Valdeir Soares",
]);

// ─── GO MS NORTE (antiga GO/MS NORTE) ───
add("GO MS NORTE", [
  "Brunno Rocha",
  "Cristiano Moreira",
  "Daniela Balduíno",
  "Emerson Feuser",
  "Letícia Bessa",
  "Lucieli Leolato",
  "Raphael Melo",
  "Victor Hugo Aguiar",
  "Victor Aguiar",                   // variação no banco
  // Aliases históricos:
  "Ricardo Carniel",                 // outro usuário Nutro (sucessor: Brunno Rocha)
  "Rafael Lionello Jannuzzi",        // outro usuário Nutro
  "Jose cezar Ferreiras Barros",     // outro usuário Nutro
]);

// ─── IC (Inteligência de Campo) ───
add("IC", [
  "Henrique Manfrin",
  "Jonathan Guimarães",
  "Linker Louvison",
  "Lucas Santos",
  "Luis Pereira",
  "Ricardo Scannavino",
  "Valtezer Hunhoff",
]);

// ─── LATAM ───
add("LATAM", [
  "Miguel Junior",
  "Nixau Macedo",
]);

// ─── MG ES SB FL (antiga MG/ES/SB/FL) ───
add("MG ES SB FL", [
  "Bruno Ávila",
  "Guilherme Zatti",
  "Ioná Rech",
  "James Ribeiro",
  "Jeferson Ferreira",
  "José Marcos",
  "Luciano Mançanares",
  "Marcela Caetano",
  "Marcela Lopes",             // mesma pessoa — confirmado por Carol/Lucimar 26/05
]);

// ─── SP/CANA (antiga SP CANA) ───
add("SP/CANA", [
  "Aijânio Silva",
  "Aijanio Silva",        // sem til (variação histórica)
  "Fabio Leal",
  "Fábio Lima",
  "Felipe Gonçalves",
  "Michel Piemonte",
  "Regina Zandonade",
]);

/**
 * Retorna a diretoria do usuário com base no nome.
 * Faz match case-insensitive e tenta prefixos progressivos se o nome exato não bater.
 * Retorna `fallback` (default: "Outros") se não encontrar.
 */
export function getDiretoria(userName: string | undefined | null, fallback = "Outros"): string {
  if (!userName) return fallback;
  const key = normalizeName(userName);
  if (!key) return fallback;

  // Match exato
  if (USER_DIRETORIA[key]) return USER_DIRETORIA[key];

  const userTokens = new Set(key.split(" ").filter(Boolean));

  // Match parcial: procura se o userName contém ou está contido em alguma chave.
  // Tambem cobre nomes reais com nome do meio extra, ex.: "Bruno Henrique de Avila"
  // vs "Bruno Avila" na planilha.
  for (const [mapKey, dir] of Object.entries(USER_DIRETORIA)) {
    if (key.includes(mapKey) || mapKey.includes(key)) return dir;
    const mapTokens = mapKey.split(" ").filter(Boolean);
    if (mapTokens.length >= 2 && mapTokens.every((token) => userTokens.has(token))) {
      return dir;
    }
  }

  return fallback;
}

/** Retorna todas as diretorias válidas (para dropdowns). */
export const DIRETORIAS = [
  "CENTRO SUL",
  "MAPITOBAPANE",
  "MT RO",
  "GO MS NORTE",
  "MG ES SB FL",
  "SP/CANA",
  "IC",
  "LATAM",
  "ICL",
];
